export type CropPictureHeaderMeta = {
  plotName: string;
  pointNumber: number;
  fieldCode: string;
};

const HEADER_COLOR = '#97c416';
const HEADER_TEXT_COLOR = '#ffffff';
const MIN_HEADER_HEIGHT = 48;
const HEADER_HEIGHT_RATIO = 0.06;

export function formatCropPictureHeader(meta: CropPictureHeaderMeta): string {
  const plot = meta.plotName.trim() || 'Parcelle';
  return `${plot} - Point ${meta.pointNumber} - ${meta.fieldCode}`;
}

function sanitizeArchiveFileName(header: string): string {
  return header
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
}

function fitHeaderFontSize(
  context: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  maxHeight: number
): number {
  let fontSize = Math.max(14, Math.round(maxHeight * 0.45));

  while (fontSize >= 12) {
    context.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
    if (context.measureText(text).width <= maxWidth) {
      return fontSize;
    }
    fontSize -= 1;
  }

  context.font = 'bold 12px system-ui, -apple-system, sans-serif';
  return 12;
}

const ARCHIVE_MAX_DIMENSION = 1920;

async function createArchiveBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, {
      resizeWidth: ARCHIVE_MAX_DIMENSION,
      resizeQuality: 'medium',
    });
  } catch {
    try {
      return await createImageBitmap(file, {
        resizeHeight: ARCHIVE_MAX_DIMENSION,
        resizeQuality: 'medium',
      });
    } catch {
      throw new Error('createImageBitmap resize unsupported');
    }
  }
}

/**
 * Draws a branded header bar above the image for local gallery archives.
 * Downscales large ImageCapture stills to avoid OOM on mobile PWA.
 */
export async function applyCropPictureHeaderOverlay(
  file: File,
  meta: CropPictureHeaderMeta
): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/gif') {
    return file;
  }

  const headerText = formatCropPictureHeader(meta);
  let bitmap: ImageBitmap | undefined;
  let canvas: HTMLCanvasElement | undefined;

  try {
    bitmap = await createArchiveBitmap(file);
    let imageWidth = bitmap.width;
    let imageHeight = bitmap.height;
    if (imageWidth > ARCHIVE_MAX_DIMENSION || imageHeight > ARCHIVE_MAX_DIMENSION) {
      const ratio = Math.min(
        ARCHIVE_MAX_DIMENSION / imageWidth,
        ARCHIVE_MAX_DIMENSION / imageHeight
      );
      imageWidth = Math.round(imageWidth * ratio);
      imageHeight = Math.round(imageHeight * ratio);
    }
    const headerHeight = Math.max(
      MIN_HEADER_HEIGHT,
      Math.round(imageHeight * HEADER_HEIGHT_RATIO)
    );

    canvas = document.createElement('canvas');
    canvas.width = imageWidth;
    canvas.height = imageHeight + headerHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }

    const padding = Math.round(headerHeight * 0.22);
    const fontSize = fitHeaderFontSize(
      context,
      headerText,
      imageWidth - padding * 2,
      headerHeight
    );

    context.fillStyle = HEADER_COLOR;
    context.fillRect(0, 0, imageWidth, headerHeight);

    context.fillStyle = HEADER_TEXT_COLOR;
    context.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
    context.textBaseline = 'middle';
    context.fillText(headerText, padding, headerHeight / 2);

    context.drawImage(bitmap, 0, 0, bitmap.width, bitmap.height, 0, headerHeight, imageWidth, imageHeight);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas!.toBlob(resolve, 'image/jpeg', 0.92);
    });

    if (!blob) {
      return file;
    }

    const archiveName = `${sanitizeArchiveFileName(headerText)}.jpg`;
    return new File([blob], archiveName, {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    bitmap?.close();
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
  }
}
